/**
 * Cross-bundle window-held state accessors -- the ONE spelling of "the browser window, if there is one" plus the
 * lazy-init collection and shared-socket slot readers the websocket/auth family parks on it. Every MFE/SPA bundle
 * carries its own copy of every module, so cross-root sharing lives on WINDOW -- and the accessor reading it needs a
 * single home BELOW the `utils/` import floor, because a utils leaf may not value-import a sibling: seven utils leaves
 * each re-spelled `documentTarget` before this leaf existed.
 *
 * ITS VALUE IMPORTS ARE CONST SIBLINGS and add no layer-ban edge: `const/string-guards.ts` is a pure leaf with no
 * value imports of its own, `const/global-namespace.ts` reads only the brand const, and the utils floor bans a
 * utils-to-utils edge, not a const-to-const one. The
 * docstring claimed "zero value-imports" until 2026-09-05, which was true of the bodies then here and became a reason
 * to hand-roll a coercer the moment one was needed -- so the property is stated as what it actually buys.
 */

import { stringOrNull } from '@federa/const/string-guards';
import { globalKey } from '@federa/const/global-namespace';
import type { FederaFirebaseBridge } from '@federa/types/ui-auth-session';
import type { SharedSocketSlotState } from '@federa/types/websocket-shared-socket-slot';

/** The window, or null outside a browser-like host (SSR, a Node test with no double installed).
 * @runtime-derived-ssot-helper: pure-leaf helper pinned below the utils layer floor -- the single spelling of the
 * browser-window probe every cross-bundle shared-state module narrows from. */
export function documentTarget(): (EventTarget & Record<string, unknown>) | null {
	const candidate = (globalThis as { window?: unknown }).window;
	if (!candidate || typeof (candidate as EventTarget).addEventListener !== 'function') return null;
	return candidate as EventTarget & Record<string, unknown>;
}

/**
 * The browsing context's URL QUERY STRING (`?id=7`), or null outside a browser-like host -- and null, never `''`, for
 * a host that has a window but no readable `location.search`, so a caller cannot confuse "no browser" with "no query".
 *
 * A SEPARATE EXPORT RATHER THAN A READ OFF {@link documentTarget}, which is arm (3) of
 * `QUAL-BROWSER-DETECTION-SSOT-BYPASS-001`'s fix spelled out: that accessor answers
 * `EventTarget & Record<string, unknown>`, so `target.location` is `unknown` and every caller wanting a TYPED member
 * would have to smuggle it through an `as` cast -- which CLAUDE.md `Code Conventions` (type-smuggling casts) bans
 * outright. The typed read therefore lives here, beside the probe, rather than at each call site.
 *
 * IT PROBES `window`, NOT the bare global location, and the difference is live: a Cloudflare Worker carries a location
 * object and no `window`, so reading the bare global would answer a script URL's query string in a runtime the caller
 * means to treat as absent. The probe matches what the inline `typeof window === 'undefined'` guards it replaces were
 * actually testing.
 *
 * THE FINAL COERCION IS {@link stringOrNull} (`const/string-guards.ts`), not an inline `typeof` ternary: `''` must
 * pass THROUGH (a browser on a bare URL genuinely has an empty query), and that is exactly the member of the coercer
 * family whose empty-string arm differs from `nonEmptyStringOrNull`'s.
 *
 * @runtime-derived-ssot-helper: pure-leaf helper pinned below the utils layer floor -- a `utils/` leaf may not
 * value-import the `utils/browser/detection.ts` sibling (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, no allowlist), which
 * is the same ground {@link documentTarget} records.
 */
export function documentLocationSearch(): string | null {
	return stringOrNull((globalThis as { window?: { location?: { search?: unknown } } }).window?.location?.search);
}

/** The browsing context's `location.pathname`, or null outside one -- {@link documentLocationSearch}'s sibling, under the
 *  same probe and the same absent-value contract. First read by a page a group's service opens on, which names its group
 *  in the path (`const/group-service-routes.ts`).
 *  @runtime-derived-ssot-helper: pinned below the utils layer floor on the same ground as {@link documentLocationSearch}. */
export function documentLocationPathname(): string | null {
	return stringOrNull((globalThis as { window?: { location?: { pathname?: unknown } } }).window?.location?.pathname);
}

/**
 * The browsing context's `localStorage`, or null outside a browser-like host and for a host that has a window but
 * exposes no storage. The ONE narrowing point: a boolean availability probe in another function cannot narrow the
 * global at the use site, so the reader RETURNS the store and every caller narrows on the value it got back.
 *
 * A SEPARATE EXPORT RATHER THAN A READ OFF {@link documentTarget}, for the same reason as
 * {@link documentLocationSearch}: that accessor answers `EventTarget & Record<string, unknown>`, so
 * `target.localStorage` is `unknown` and each caller would have to smuggle `Storage` back through an `as` cast --
 * arm (3) of `QUAL-BROWSER-DETECTION-SSOT-BYPASS-001`'s fix, which puts the typed member read here beside the probe.
 *
 * IT PROBES `window.localStorage`, NOT the bare global, and the difference is the one both carriers it replaced were
 * testing: a Worker exposing a storage-shaped global but no `window` is not a browsing context and must read as
 * absent. THE ACCESS IS NOT WRAPPED: reading the property is not what throws -- `getItem`/`setItem` are, in
 * private-mode and sandboxed-iframe contexts -- and both carriers wrapped exactly those calls, never this read, so
 * swallowing here would convert a real storage fault into a silent "no browser".
 *
 * @runtime-derived-ssot-helper: pure-leaf helper pinned below the utils layer floor -- a `utils/` leaf may not
 * value-import the `utils/browser/detection.ts` sibling (`QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001`, no allowlist), which
 * is the same ground {@link documentTarget} records.
 */
export function documentLocalStorage(): Storage | null {
	return (globalThis as { window?: { localStorage?: Storage } }).window?.localStorage ?? null;
}

/**
 * The Firebase bridge the SHELL published on window -- its SDK functions and its one Auth instance -- or undefined
 * outside a browser and in a document whose shell published none. Every MFE bundle and every auth reader resolves the
 * shell's session through this, never through a second SDK instance (which re-restores the session and breaks logout);
 * six readers each spelled the `Reflect.get` for themselves.
 *
 * `Reflect.get` reaches the runtime-composed key without widening `Window`: {@link globalKey} is a runtime call, and an
 * ambient `Window` member name must be a literal TYPE.
 *
 * @runtime-derived-ssot-helper: pure-leaf helper pinned below the utils layer floor -- `utils/websocket/ws-auth-token.ts`
 * reads it too, and a utils leaf may not value-import a utils sibling, the same ground {@link documentTarget} records.
 */
export function publishedFirebaseBridge(): FederaFirebaseBridge | undefined {
	const host = (globalThis as { window?: object }).window;
	if (host === undefined) return undefined;
	return Reflect.get(host, globalKey('Firebase')) as FederaFirebaseBridge | undefined;
}

/** Window-held collection accessor, created on first use; null only when there is no document.
 * @runtime-derived-ssot-helper: pure-leaf helper pinned below the utils layer floor -- the lazy-init pattern every
 * cross-bundle registry (handler sets, dispatcher sets, subscription maps) shares. */
export function heldWindowCollection<T>(key: string, create: () => T): T | null {
	const target = documentTarget();
	if (!target) return null;
	const existing = target[key];
	if (existing) return existing as T;
	const made = create();
	target[key] = made;
	return made;
}

/** Register `member` in the window-held Set at `key`, answering an unregister safe to call more than once (and a no-op
 * outside a browser host). ONE home for the add/return-remover body every cross-bundle registry repeated verbatim --
 * explore dispatchers, per-property dispatchers, feed-event handlers.
 * @runtime-derived-ssot-helper: pure-leaf helper pinned below the utils layer floor over the same-file
 * {@link heldWindowCollection} store. */
export function addToHeldSet<T>(key: string, member: T): () => void {
	const set = heldWindowCollection(key, () => new Set<T>());
	if (!set) return () => undefined;
	set.add(member);
	return () => {
		set.delete(member);
	};
}

/** Invoke `deliver` for every member of the window-held Set at `key`. A thrower is ISOLATED but RETHROWN on a fresh
 * task, so the loop finishes for the remaining members and the throw still reaches the global handler -- the fan-out
 * contract each shared-socket leaf documents for itself and previously re-spelled.
 * @runtime-derived-ssot-helper: pure-leaf helper pinned below the utils layer floor over the same-file
 * {@link heldWindowCollection} store. */
export function fanOutHeldSet<T>(key: string, deliver: (member: T) => void): void {
	const set = heldWindowCollection(key, () => new Set<T>());
	if (!set) return;
	for (const member of set) {
		try {
			deliver(member);
		} catch (error) {
			queueMicrotask(() => {
				throw error;
			});
		}
	}
}

/** The refcounted shared-socket slot for `key`, created on first use. ONE home for the initializer literal, so the
 * claim/publish side (`utils/websocket/shared-socket-slot.ts`), the acquire/release side (`shared-socket-refs.ts`) and the force-down side
 * (`utils/websocket/shared-socket-retire.ts`) can never mint diverging slots. `retireListeners` is deliberately NOT in
 * the initializer: the retire side is its only reader and creates it on demand.
 * @runtime-derived-ssot-helper: pure-leaf helper pinned below the utils layer floor over the same window store. */
export function sharedSocketSlot(key: string): SharedSocketSlotState | null {
	return heldWindowCollection<SharedSocketSlotState>(key, () => ({
		client: null,
		userId: null,
		refs: 0,
		claimed: false,
		waiters: new Set(),
	}));
}
